/**
 * Cross-app navigation between Claims (837) and Remittance Advice (ERA/EOB).
 * Used by ClaimsProcessing, RemittanceAdvice, DenialManagement, ARPosting.
 *
 * When embedded in the RCM shell (or any parent iframe), detail links open an
 * in-page iframe modal — never a new browser window.
 */

export const RCM_OPEN_CROSS_APP_EVENT = 'rcm:open-cross-app';
export const RCM_OPEN_IFRAME_MESSAGE = 'RCM_OPEN_IFRAME';
export const RCM_CLOSE_IFRAME_MESSAGE = 'RCM_CLOSE_IFRAME';

function isLocalHost(hostname) {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)
  );
}

function isBareIp(hostname) {
  return /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
}

function isEmbedded() {
  try {
    return typeof window !== 'undefined' && window.parent && window.parent !== window;
  } catch {
    return true;
  }
}

/** True when this document is inside a shell iframe (not the top window). */
export function isNestedInShell() {
  return isEmbedded();
}

/**
 * True when the current page was opened as an embed (query param).
 * Used by detail pages to hide list navigation / close the overlay instead.
 */
export function isEmbeddedDetailMode() {
  if (typeof window === 'undefined') return false;
  try {
    const raw = new URLSearchParams(window.location.search).get('embedded');
    const v = String(raw || '').trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'yes';
  } catch {
    return false;
  }
}

/**
 * Ask the parent overlay host to close the cross-app iframe modal.
 * Safe no-op when not nested.
 */
export function closeCrossAppOverlay() {
  if (typeof window === 'undefined') return;
  const payload = { type: RCM_CLOSE_IFRAME_MESSAGE };
  try {
    window.dispatchEvent(new CustomEvent(RCM_CLOSE_IFRAME_MESSAGE, { detail: payload }));
  } catch {
    /* ignore */
  }
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(payload, '*');
    }
  } catch {
    /* ignore */
  }
  try {
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(payload, '*');
    }
  } catch {
    /* ignore */
  }
}

function getOrgFromStorage() {
  if (typeof window === 'undefined') return null;
  return (
    new URLSearchParams(window.location.search).get('organization_id') ||
    localStorage.getItem('selectedOrganizationId') ||
    localStorage.getItem('organizationId') ||
    localStorage.getItem('organization_id') ||
    null
  );
}

function applyAuthQueryParams(url) {
  if (typeof window === 'undefined') return;
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  if (token) url.searchParams.set('auth_token', token);
  const refresh = localStorage.getItem('refresh_token');
  if (refresh) url.searchParams.set('refresh_token', refresh);
}

/**
 * Frontend port → DNS label for the per-customer subdomain deployment.
 * MUST stay in sync with getServicePathForPort() in AgenticRCM's ServiceFrame
 * and the `map $svc $svc_port` block in the edge nginx config.
 */
const PORT_TO_SLUG = {
  3010: 'edi-platform',
  3050: 'activity-log',
  3051: 'task-manager',
  3100: 'patients',
  3101: 'practitioners',
  3102: 'claims-core',
  3110: 'patient-account',
  3150: 'insurance-verification',
  3151: 'prior-authorization',
  3152: 'charge-capture',
  3153: 'claims-processing',
  3154: 'denial-management',
  3155: 'ar-posting',
  3156: 'contract-management',
  3157: 'remittance-advice',
  3158: 'rcm-compliance',
  3060: 'integrations',
};

/**
 * Origin of a sibling service, correct for every deployment mode:
 *   localhost / bare IP        → <proto>//<host>:<port>        (unchanged)
 *   service subdomain (4+ lbl) → <proto>//<slug>.<customer>.<apex>
 *   shell domain      (3 lbl)  → <proto>//<slug>.<host>
 * Never reuses the current host with another service's port — that port has no TLS.
 */
export function getServiceOriginForPort(port) {
  if (typeof window === 'undefined') return '';
  const { protocol, hostname } = window.location;
  if (isLocalHost(hostname)) {
    return `${protocol}//${hostname}:${port}`;
  }
  const slug = PORT_TO_SLUG[Number(port)];
  if (!slug) return `${protocol}//${hostname}`;
  const labels = hostname.split('.');
  // On a service subdomain, strip the leading label to get the customer base.
  const customerBase = labels.length >= 4 ? labels.slice(1).join('.') : hostname;
  return `${protocol}//${slug}.${customerBase}`;
}

function serviceBase(envVar, localPort, embedPath) {
  const override = process.env[envVar];
  if (override && String(override).trim()) {
    return String(override).replace(/\/$/, '');
  }
  if (typeof window === 'undefined') return '';
  return getServiceOriginForPort(localPort);
}

export function getClaimsProcessingBase() {
  return serviceBase(
    'REACT_APP_CLAIMS_PROCESSING_URL',
    '3153',
    '/services/claims-processing'
  );
}

export function getRemittanceAdviceBase() {
  return serviceBase(
    'REACT_APP_REMITTANCE_ADVICE_URL',
    '3157',
    '/services/remittance-advice'
  );
}

export function getArPostingBase() {
  return serviceBase(
    'REACT_APP_AR_POSTING_URL',
    '3155',
    '/services/ar-posting'
  );
}

function buildServicePageUrl(base, path, organizationId, options = {}) {
  const org = organizationId || getOrgFromStorage();
  const rel = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${String(base).replace(/\/$/, '')}${rel}`, window.location.origin);
  if (org) url.searchParams.set('organization_id', org);
  if (options.embedded) {
    url.searchParams.set('embedded', 'true');
    url.searchParams.set('show_sidebar', '0');
  }
  if (options.includeAuth) {
    applyAuthQueryParams(url);
  }
  return url.toString();
}

export function getIntegrationsServiceBase() {
  return serviceBase(
    'REACT_APP_INTEGRATIONS_URL',
    '3060',
    '/integrations'
  );
}

export function getDenialManagementBase() {
  return serviceBase(
    'REACT_APP_DENIAL_MANAGEMENT_URL',
    '3154',
    '/services/denial-management'
  );
}

export function getContractManagementBase() {
  return serviceBase(
    'REACT_APP_CONTRACT_MANAGEMENT_URL',
    '3156',
    '/services/contract-management'
  );
}

/** Contract Management API base (includes /api/v1). */
export function getContractManagementApiBase() {
  if (typeof window !== 'undefined' && window.__CM_API_V1_BASE__) {
    return window.__CM_API_V1_BASE__;
  }

  const envUrl = process.env.REACT_APP_CONTRACT_MANAGEMENT_API_URL;
  if (envUrl && String(envUrl).trim()) {
    const clean = String(envUrl).trim().replace(/\/+$/, '');
    return clean.endsWith('/api/v1') ? clean : `${clean}/api/v1`;
  }

  if (typeof window === 'undefined') {
    return 'http://localhost:3156/api/v1';
  }

  const loc = window.location;
  const port = loc.port || (loc.protocol === 'https:' ? '443' : '80');
  const host = loc.hostname;

  if (port === '3160' || port === '3161') {
    return '/rcm-api/contract-management/api/v1';
  }

  if (host.startsWith('contract-management.')) {
    return '/api/v1';
  }

  if (!isLocalHost(host) && !isBareIp(host) && (port === '443' || port === '80')) {
    return `${getContractManagementBase()}/api/v1`;
  }

  if (isLocalHost(host) || isBareIp(host)) {
    return `${loc.protocol}//${host}:3156/api/v1`;
  }

  return `${getContractManagementBase()}/api/v1`;
}

/**
 * @param {string} claimId
 * @param {string} [organizationId]
 * @param {{ embedded?: boolean, includeAuth?: boolean }} [options]
 */
export function buildClaimDetailUrl(claimId, organizationId, options = {}) {
  const org = organizationId || getOrgFromStorage();
  const base = getClaimsProcessingBase();
  const url = new URL(`${base}/claims/${encodeURIComponent(claimId)}`, window.location.origin);
  if (org) url.searchParams.set('organization_id', org);
  if (options.embedded) {
    url.searchParams.set('embedded', 'true');
    url.searchParams.set('show_sidebar', '0');
  }
  if (options.includeAuth) {
    applyAuthQueryParams(url);
  }
  return url.toString();
}

/**
 * @param {string} eobId
 * @param {string} [organizationId]
 * @param {{ embedded?: boolean, includeAuth?: boolean }} [options]
 */
export function buildEobDetailUrl(eobId, organizationId, options = {}) {
  const org = organizationId || getOrgFromStorage();
  const base = getRemittanceAdviceBase();
  const url = new URL(`${base}/eobs/${encodeURIComponent(eobId)}`, window.location.origin);
  if (org) url.searchParams.set('organization_id', org);
  if (options.embedded) {
    url.searchParams.set('embedded', 'true');
    url.searchParams.set('show_sidebar', '0');
  }
  if (options.includeAuth) {
    applyAuthQueryParams(url);
  }
  return url.toString();
}

/**
 * Remittance Advice EOB / ERA list (canonical remittance list).
 * @param {string} [organizationId]
 * @param {{ embedded?: boolean, includeAuth?: boolean }} [options]
 */
export function buildEobListUrl(organizationId, options = {}) {
  const org = organizationId || getOrgFromStorage();
  const base = getRemittanceAdviceBase();
  const url = new URL(`${base}/eobs`, window.location.origin);
  if (org) url.searchParams.set('organization_id', org);
  if (options.embedded) {
    url.searchParams.set('embedded', 'true');
    url.searchParams.set('show_sidebar', '0');
  }
  if (options.includeAuth) {
    applyAuthQueryParams(url);
  }
  return url.toString();
}

function postRcmNavigate(path, organizationId) {
  try {
    if (isEmbedded()) {
      window.parent.postMessage(
        {
          type: 'RCM_NAVIGATE',
          path,
          organization_id: organizationId || undefined,
        },
        '*'
      );
    }
  } catch {
    /* cross-origin parent */
  }
}

function openCrossAppDetail({ kind, id, url, title, organizationId }) {
  const detail = { kind, id, url, title, organizationId: organizationId || undefined };

  // Nested in RCM shell (or another host iframe): open overlay on the parent so
  // the backdrop covers the full shell, not only the current micro-app pane.
  if (isEmbedded()) {
    try {
      window.parent.postMessage(
        {
          type: RCM_OPEN_IFRAME_MESSAGE,
          ...detail,
        },
        '*'
      );
    } catch {
      /* ignore */
    }
    try {
      if (window.top && window.top !== window.parent) {
        window.top.postMessage(
          {
            type: RCM_OPEN_IFRAME_MESSAGE,
            ...detail,
          },
          '*'
        );
      }
    } catch {
      /* ignore */
    }
    return;
  }

  // Top-level window: prefer in-app overlay (CrossAppNavHost).
  try {
    window.dispatchEvent(new CustomEvent(RCM_OPEN_CROSS_APP_EVENT, { detail }));
  } catch {
    /* ignore */
  }

  if (typeof window !== 'undefined' && window.__RCM_CROSS_APP_HOST__) {
    return;
  }

  // Standalone tab with no host: same-tab navigation (never window.open).
  window.location.assign(url);
}

export function openClaimDetail(claimId, organizationId) {
  if (!claimId) return;
  const org = organizationId || getOrgFromStorage();
  const url = buildClaimDetailUrl(claimId, org, { embedded: true, includeAuth: true });
  postRcmNavigate(`/claims/${claimId}`, org);
  openCrossAppDetail({
    kind: 'claim',
    id: claimId,
    url,
    title: `Claim ${claimId}`,
    organizationId: org,
  });
}

export function openEobDetail(eobId, organizationId) {
  if (!eobId) return;
  const org = organizationId || getOrgFromStorage();
  const url = buildEobDetailUrl(eobId, org, { embedded: true, includeAuth: true });
  postRcmNavigate(`/eobs/${eobId}`, org);
  openCrossAppDetail({
    kind: 'eob',
    id: eobId,
    url,
    title: `ERA ${String(eobId).slice(0, 8)}…`,
    organizationId: org,
  });
}

/** Open Integrations Service (bank/PMS/clearinghouse config). Not an AR Posting page. */
export function openIntegrationsService(organizationId) {
  const org = organizationId || getOrgFromStorage();
  const base = getIntegrationsServiceBase();
  const url = new URL(base ? `${String(base).replace(/\/$/, '')}/` : '/integrations', window.location.origin);
  if (org) url.searchParams.set('organization_id', org);
  url.searchParams.set('embedded', 'true');
  postRcmNavigate('/integrations', org);
  openCrossAppDetail({
    kind: 'integrations',
    id: 'integrations',
    url: url.toString(),
    title: 'Integrations',
    organizationId: org,
  });
}

/** Open Remittance Advice EOB / ERA list (canonical remittance list). */
export function openEobList(organizationId) {
  openRemittanceAdvicePage('/eobs', organizationId, 'Remittance EOBs');
}

/** Open a Remittance Advice page (ingest / what the 835 said). */
export function openRemittanceAdvicePage(path = '/eobs', organizationId, title = 'Remittance Advice') {
  const org = organizationId || getOrgFromStorage();
  const url = buildServicePageUrl(getRemittanceAdviceBase(), path || '/eobs', org, {
    embedded: true,
    includeAuth: true,
  });
  postRcmNavigate('/remittance-advice', org);
  openCrossAppDetail({
    kind: 'eob-list',
    id: path || 'eob-list',
    url,
    title,
    organizationId: org,
  });
}

/** Open an AR Posting page (cash posting / wrap posting / claim adjustments). */
export function openArPostingPage(path = '/', organizationId, title = 'AR Posting') {
  const org = organizationId || getOrgFromStorage();
  const url = buildServicePageUrl(getArPostingBase(), path || '/', org, {
    embedded: true,
    includeAuth: true,
  });
  postRcmNavigate('/ar-posting', org);
  openCrossAppDetail({
    kind: 'ar-posting',
    id: path || 'ar-posting',
    url,
    title,
    organizationId: org,
  });
}

/** Open any RCM micro-app page in the shell overlay (work-queue deep links). */
export function openRcmServicePage(frontendPort, path = '/', organizationId, title = 'RCM') {
  const org = organizationId || getOrgFromStorage();
  const url = buildServicePageUrl(getServiceOriginForPort(frontendPort), path || '/', org, {
    embedded: true,
    includeAuth: true,
  });
  openCrossAppDetail({
    kind: 'rcm-service',
    id: path || String(frontendPort),
    url,
    title,
    organizationId: org,
  });
}

/**
 * @param {string} claimId
 * @param {string} [organizationId]
 * @param {{ embedded?: boolean, includeAuth?: boolean }} [options]
 */
export function buildDenialDetailUrl(claimId, organizationId, options = {}) {
  const org = organizationId || getOrgFromStorage();
  const base = getDenialManagementBase();
  const url = new URL(`${base}/claims/${encodeURIComponent(claimId)}`, window.location.origin);
  if (org) url.searchParams.set('organization_id', org);
  if (options.embedded) {
    url.searchParams.set('embedded', 'true');
    url.searchParams.set('show_sidebar', '0');
  }
  if (options.includeAuth) {
    applyAuthQueryParams(url);
  }
  return url.toString();
}

/** Open Denial Management claim detail (CLP02=4 / denial workflow). */
export function openDenialDetail(claimId, organizationId) {
  if (!claimId) return;
  const org = organizationId || getOrgFromStorage();
  const url = buildDenialDetailUrl(claimId, org, { embedded: true, includeAuth: true });
  postRcmNavigate(`/claims/${claimId}`, org);
  openCrossAppDetail({
    kind: 'denial',
    id: claimId,
    url,
    title: `Denial ${claimId}`,
    organizationId: org,
  });
}

function appendEmbeddedOrgParams(url, organizationId, options = {}) {
  const org = organizationId || getOrgFromStorage();
  if (org) url.searchParams.set('organization_id', org);
  if (options.embedded) {
    url.searchParams.set('embedded', 'true');
    url.searchParams.set('show_sidebar', '0');
  }
  if (options.includeAuth) {
    applyAuthQueryParams(url);
  }
  return url;
}

/**
 * @param {string} contractId
 * @param {string} [organizationId]
 * @param {{ embedded?: boolean, includeAuth?: boolean }} [options]
 */
export function buildContractDetailUrl(contractId, organizationId, options = {}) {
  const base = getContractManagementBase();
  const url = new URL(
    `${base}/contracts/${encodeURIComponent(contractId)}`,
    window.location.origin
  );
  return appendEmbeddedOrgParams(url, organizationId, options).toString();
}

/**
 * @param {{ organizationId?: string, payerId?: string, contractId?: string, feeScheduleId?: string, tab?: string }} params
 * @param {{ embedded?: boolean, includeAuth?: boolean }} [options]
 */
export function buildFeeScheduleUrl(params = {}, options = {}) {
  const {
    organizationId,
    payerId,
    contractId,
    feeScheduleId,
    tab = payerId || contractId ? 'payer-contract' : undefined,
  } = params;
  const base = getContractManagementBase();
  const url = new URL(`${base}/fee-schedules`, window.location.origin);
  if (tab) url.searchParams.set('tab', tab);
  if (payerId) url.searchParams.set('payer_id', String(payerId));
  if (contractId) url.searchParams.set('contract_id', String(contractId));
  if (feeScheduleId) url.searchParams.set('fee_schedule_id', String(feeScheduleId));
  return appendEmbeddedOrgParams(url, organizationId, options).toString();
}

export function openContractDetail(contractId, organizationId, title) {
  if (!contractId) return;
  const org = organizationId || getOrgFromStorage();
  const url = buildContractDetailUrl(contractId, org, { embedded: true, includeAuth: true });
  postRcmNavigate(`/contract-management/contracts/${contractId}`, org);
  openCrossAppDetail({
    kind: 'contract',
    id: contractId,
    url,
    title: title || 'Payer contract',
    organizationId: org,
  });
}

export function openFeeSchedule(params = {}, title) {
  const org = params.organizationId || getOrgFromStorage();
  const url = buildFeeScheduleUrl(params, { embedded: true, includeAuth: true });
  const payerId = params.payerId ? String(params.payerId) : '';
  postRcmNavigate(
    `/contract-management/fee-schedules${payerId ? `?payer_id=${encodeURIComponent(payerId)}` : ''}`,
    org
  );
  openCrossAppDetail({
    kind: 'fee-schedule',
    id: params.feeScheduleId || params.contractId || params.payerId || 'fee-schedule',
    url,
    title: title || 'Fee schedule',
    organizationId: org,
  });
}

export function lineageTypeLabel(lineageType) {
  if (!lineageType) return '837';
  const t = String(lineageType).toLowerCase();
  if (t === 'original') return 'Original 837';
  if (t === 'resubmit' || t === 'resubmission') return 'Resubmit 837';
  if (t === 'corrected') return 'Corrected 837';
  if (t === 'replacement') return 'Replacement 837';
  return lineageType;
}

/** Resolve original | replacement | void from ERA-linked 837 metadata. */
export function resolveSubmissionTag(claim) {
  if (!claim) return 'original';
  if (claim.submission_tag) return String(claim.submission_tag).toLowerCase();
  const freq = String(claim.claim_frequency_code || '').trim();
  if (freq === '8') return 'void';
  if (freq === '7') return 'replacement';
  if (freq === '1') return 'original';
  const lineage = String(claim.lineage_type || '').toLowerCase();
  if (lineage === 'void') return 'void';
  if (['resubmit', 'resubmission', 'corrected', 'replacement'].includes(lineage)) return 'replacement';
  const seq = Number(claim.submission_sequence || 1);
  return seq > 1 ? 'replacement' : 'original';
}

export function submissionTagLabel(tag) {
  const t = String(tag || 'original').toLowerCase();
  if (t === 'void') return 'Void (CLM05-8)';
  if (t === 'replacement') return 'Replacement (CLM05-7)';
  return 'Original (CLM05-1)';
}

export function submissionTagBadgeClass(tag, { isMatched = false } = {}) {
  const t = String(tag || 'original').toLowerCase();
  const base =
    t === 'void'
      ? 'border-red-300 bg-red-50 text-red-900'
      : t === 'replacement'
        ? 'border-amber-400 bg-amber-50 text-amber-950'
        : 'border-blue-300 bg-blue-50 text-blue-900';
  if (isMatched) {
    return `${base} ring-2 ring-green-500 border-green-500`;
  }
  return base;
}

export function pickMatchedLinkedClaim(linkedClaims, matchedClaimId) {
  const claims = linkedClaims || [];
  if (!claims.length) return null;
  if (matchedClaimId) {
    const hit = claims.find((c) => String(c.claim_id) === String(matchedClaimId));
    if (hit) return hit;
  }
  return claims.find((c) => c.is_matched) || claims[0];
}

export function lineageFamilySummary(linkedClaims) {
  const claims = linkedClaims || [];
  if (claims.length <= 1) return null;
  const tags = claims.map((c) => resolveSubmissionTag(c));
  const originals = tags.filter((t) => t === 'original').length;
  const replacements = tags.filter((t) => t === 'replacement').length;
  const voids = tags.filter((t) => t === 'void').length;
  const parts = [];
  if (originals) parts.push(`${originals} original`);
  if (replacements) parts.push(`${replacements} replacement`);
  if (voids) parts.push(`${voids} void`);
  return `${claims.length}-submission family (${parts.join(', ')})`;
}
